import React, { useState, useEffect } from 'react';
import type { CountdownTimerComponent } from '../../../types/builder';

interface CountdownTimerRendererProps {
  component: CountdownTimerComponent;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export const CountdownTimerRenderer: React.FC<CountdownTimerRendererProps> = ({ component }) => {
  const { settings } = component;
  
  const calculateTimeLeft = (): TimeLeft => {
    const difference = new Date(settings.targetDate).getTime() - Date.now();
    
    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
    
    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  };

  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [settings.targetDate]);

  const TimeBox: React.FC<{ value: number; label: string }> = ({ value, label }) => (
    <div className="flex flex-col items-center flex-shrink-0">
      <div
        className="text-lg font-bold px-2 py-2 rounded-md min-w-[50px] flex items-center justify-center"
        style={{
          backgroundColor: settings.boxBackgroundColor,
          color: settings.timerColor,
        }}
      >
        {value.toString().padStart(2, '0')}
      </div>
      <div className="text-[9px] font-medium mt-1 uppercase tracking-wide">
        {label}
      </div>
    </div>
  );

  return (
    <div
      style={{
        backgroundColor: settings.backgroundColor,
        color: settings.textColor,
        padding: `${settings.padding}px`,
        textAlign: settings.alignment,
      }}
    >
      <div className="max-w-2xl mx-auto">
        {settings.title && (
          <h2 className="text-base font-bold mb-3">{settings.title}</h2>
        )}
        
        <div className="flex items-center justify-center gap-1.5 flex-nowrap overflow-x-auto">
          {settings.showDays && (
            <TimeBox value={timeLeft.days} label={settings.labels.days} />
          )}
          
          {settings.showHours && (
            <TimeBox value={timeLeft.hours} label={settings.labels.hours} />
          )}
          
          {settings.showMinutes && (
            <TimeBox value={timeLeft.minutes} label={settings.labels.minutes} />
          )}
          
          {settings.showSeconds && (
            <TimeBox value={timeLeft.seconds} label={settings.labels.seconds} />
          )}
        </div>
      </div>
    </div>
  );
};

